/**
 * Manual-task creation modal.
 *
 * Wraps POST /api/v1/tasks (previously orphan — no FE caller existed).
 * Self-contained: fetch nothing, mutate nothing besides the create
 * call. The caller decides whether to refetch a parent list on success.
 *
 * Today wired from agent ClientDetail (QuickActions → "+ New task")
 * with the client pre-filled. The same modal slots cleanly into the
 * agent dashboard or staff case detail in a later pass — pass the
 * client id (and an optional commitment id) and the form does the rest.
 *
 * Auto-generated task kinds (commitment_overdue / _renewal /
 * kyc_expiring) are NOT exposed here — those come from background
 * jobs. The kind is hardcoded to 'manual' so an agent can't
 * accidentally create a synthetic "overdue" task that the system
 * would later try to reconcile against a real commitment.
 */
import { useEffect, useState } from 'react';
import tasksService from '../../services/tasks.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { useMovablePopup } from '../MovablePopup';

interface Props {
  /** Pre-fill the task with this client. */
  clientId: string;
  /** Display in the modal header for context. */
  clientName: string;
  /** Optional — link the task to a specific commitment (used by
   *  follow-up flows on /agent/commitments). */
  commitmentId?: string | null;
  onClose: () => void;
  /** Fired after a successful create. Caller decides whether to
   *  refetch a parent list. */
  onCreated: (taskId: string) => void;
}

type Priority = 'low' | 'normal' | 'high' | 'urgent';

const PRIORITY_OPTIONS: Priority[] = ['low', 'normal', 'high', 'urgent'];

function todayPlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
}

export default function NewTaskModal({
  clientId,
  clientName,
  commitmentId,
  onClose,
  onCreated,
}: Readonly<Props>): JSX.Element {
  const popup = useMovablePopup<HTMLFormElement>();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('normal');
  // Default due date = 3 days from today. Agents can shift it or clear
  // it. Open-ended tasks are valid; they just don't appear in the
  // "due today / overdue" Act-now bucket on the dashboard.
  const [dueDate, setDueDate] = useState<string>(todayPlusDays(3));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC closes (only when not submitting so we don't drop a half-done
  // network call without feedback). Same pattern as AssistanceModal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) {
      setError('Title is required.');
      return;
    }
    setSubmitting(true);
    try {
      const { task_id } = await tasksService.createTask({
        title: trimmedTitle,
        description: description.trim() || null,
        kind: 'manual',
        priority,
        client_id: clientId,
        commitment_id: commitmentId ?? null,
        // Empty string from the date input means "no due date" — null
        // is the right wire format.
        due_date: dueDate || null,
      });
      onCreated(task_id);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not create the task. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)' }}
    >
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="fixed inset-0"
        style={{ background: 'transparent', border: 'none', cursor: 'default', padding: 0 }}
      />
      <form
        ref={popup.panelRef}
        onSubmit={handleSubmit}
        className="rounded-2xl w-full max-w-md relative z-10"
        style={{
          ...popup.panelStyle,
          background: colors.surface,
          padding: '24px 24px 20px',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        }}
      >
        {popup.bar}
        <h2
          className="text-lg font-semibold mb-1"
          style={{ color: colors.ink }}
        >
          New follow-up task
        </h2>
        <p className="text-sm mb-4" style={{ color: colors.muted }}>
          For <strong>{clientName}</strong>. Shows up in your Act-now
          queue when it&apos;s due or overdue.
        </p>

        <div className="mb-3">
          <label
            htmlFor="task-title"
            className="block text-xs font-semibold mb-1"
            style={{ color: colors.inkSoft }}
          >
            Title <span style={{ color: colors.danger }}>*</span>
          </label>
          <input
            id="task-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={500}
            autoFocus
            placeholder="e.g. Call about a term that ends soon"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{
              border: `1px solid ${colors.border}`,
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div className="mb-3">
          <label
            htmlFor="task-description"
            className="block text-xs font-semibold mb-1"
            style={{ color: colors.inkSoft }}
          >
            Notes
          </label>
          <textarea
            id="task-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={5000}
            placeholder="Anything the older you needs to remember about this call (optional)"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{
              border: `1px solid ${colors.border}`,
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label
              htmlFor="task-priority"
              className="block text-xs font-semibold mb-1"
              style={{ color: colors.inkSoft }}
            >
              Priority
            </label>
            <select
              id="task-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as Priority)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{
                border: `1px solid ${colors.border}`,
                boxSizing: 'border-box',
              }}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="task-due"
              className="block text-xs font-semibold mb-1"
              style={{ color: colors.inkSoft }}
            >
              Due date
            </label>
            <input
              id="task-due"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={todayPlusDays(0)}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{
                border: `1px solid ${colors.border}`,
                boxSizing: 'border-box',
              }}
            />
            <p className="text-[10px] mt-1" style={{ color: colors.muted }}>
              Leave blank for open-ended (won&apos;t appear in Act-now).
            </p>
          </div>
        </div>

        {error && (
          <div
            className="mt-3 p-3 rounded-lg text-sm"
            style={{
              background: colors.dangerBg,
              border: `1px solid ${colors.dangerBorder}`,
              color: colors.danger,
            }}
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{
              background: colors.surface,
              color: colors.inkSoft,
              border: `1px solid ${colors.border}`,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || title.trim().length === 0}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{
              background: title.trim().length === 0 ? colors.disabledBg : colors.navy,
              color: title.trim().length === 0 ? colors.disabledText : '#FFFFFF',
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            {submitting ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </form>
    </div>
  );
}
